/**
 * PublishingCardQT — right-rail summary for a Quick Tap. Sibling of
 * the session PublishingCard with quick-tap field names and no
 * captions row (Quick Taps don't ship caption tracks).
 *
 *   • Access  — Premium / Free pill (from `quick_tap_is_premium`)
 *   • Length  — `quick_tap_duration` (MM:SS) + raw seconds
 *   • Created — `quick_tap_created_at`
 *   • Last updated — `quick_tap_updated_at`
 *   • Featured — solid teal pill when `quick_tap_is_featured` is on,
 *               muted "Not featured" otherwise. Featured status is
 *               quick-tap-specific and surfaces here because it's
 *               the closest analogue to a publishing flag the
 *               session card surfaces with the Active pill.
 *
 * Activate / Deactivate link at the bottom wires through the same
 * setter the SettingsCardQT uses so both surfaces stay in sync.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import TierPill from '../../redesign/TierPill';

function formatLong(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatShortDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function PublishingCardQT({ formData, onToggleActive }) {
  const isActive = !!formData.quick_tap_is_active;
  const isPremium = !!formData.quick_tap_is_premium;
  const isFeatured = !!formData.quick_tap_is_featured;
  const lengthSec = Number(formData.quick_tap_duration_in_sec) || 0;
  const lengthLabel = formData.quick_tap_duration
    ? `${formData.quick_tap_duration}${lengthSec ? ` (${lengthSec} sec)` : ''}`
    : '—';

  return (
    <RailCard
      variant="publish"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Publishing"
          variant="publish"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-condensed font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '3px 9px',
                borderRadius: 999,
                background: isActive ? '#75A29333' : '#9CA1A81F',
                color: isActive ? '#5C7E72' : '#9CA1A8',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '14px 18px' }}>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px 14px',
            margin: 0,
          }}
        >
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Access
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              {/* TierPill takes `isFree` — quick-taps use the inverse
                  flag (`is_premium`), so flip it on the way in. */}
              <TierPill isFree={!isPremium} />
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Length
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {lengthLabel}
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Created
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {formatLong(formData.quick_tap_created_at)}
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Last updated
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {formatShortDateTime(formData.quick_tap_updated_at)}
            </dd>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Featured
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: isFeatured ? '#75A29333' : '#9CA1A81F',
                  color: isFeatured ? '#5C7E72' : '#9CA1A8',
                  whiteSpace: 'nowrap',
                }}
              >
                {isFeatured ? 'On featured shelf' : 'Not featured'}
              </span>
            </dd>
          </div>
        </dl>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #ECE3D2',
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => onToggleActive?.(!isActive)}
            className="font-semibold cursor-pointer"
            style={{
              fontSize: 12,
              color: isActive ? '#C4715B' : '#5C7E72',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            {isActive ? 'Deactivate quick tap' : 'Activate quick tap'}
          </button>
        </div>
      </div>
    </RailCard>
  );
}
